import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { db, supabase } from '../services/supabase';
import { paymentService } from '../services/paymentService';

const CANCEL_REASONS = [
  { id: 'expensive', label: "It's too expensive", icon: '💸' },
  { id: 'not_using', label: "I'm not using it enough", icon: '😴' },
  { id: 'missing_features', label: 'Missing features I need', icon: '🔧' },
  { id: 'alternative', label: 'Switched to another tool', icon: '🔄' },
  { id: 'other', label: 'Other reason', icon: '💬' },
];

const RETENTION_MESSAGES = {
  expensive: {
    headline: 'We get it — every pound counts.',
    body: "At less than the price of two coffees a month, Pro pays for itself the moment you ship faster. But we'd rather keep you than lose you.",
    offer: 'Your next month is already paid. Why not make the most of it before deciding?',
  },
  not_using: {
    headline: "Maybe we can help you get more out of it.",
    body: "Most users don't realize they can generate full project prompts, design specs, and AI-ready files in minutes. It gets powerful once you try the dashboard.",
    offer: 'Give it one more project — we think you\'ll see the difference.',
  },
  missing_features: {
    headline: "Your feedback matters to us.",
    body: "We ship updates every week based on what users like you tell us. Cancel now and you might miss the exact feature you need landing next sprint.",
    offer: 'Stay on and we\'ll prioritize your request.',
  },
  alternative: {
    headline: 'Fair enough — but consider this.',
    body: "We're the only tool that combines prompt optimization, AI project planning, and design specs in one place. Most alternatives only cover one piece.",
    offer: 'Keep access through your current billing period and compare side by side.',
  },
  other: {
    headline: "We're sorry to see you go.",
    body: "If there's anything we could do better, we'd love to hear it. Your feedback helps us build a better product for everyone.",
    offer: 'Your current period is already paid — you won\'t lose anything by staying.',
  },
};

const Profile = () => {
  const { user, isAuthenticated, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [fullName, setFullName] = useState('');
  const [username, setUsername] = useState('');
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [planLabel, setPlanLabel] = useState('Free');
  const [cancelLoading, setCancelLoading] = useState(false);
  const [upgradeLoading, setUpgradeLoading] = useState(false);
  const [lastPayment, setLastPayment] = useState(null);
  const [isSubscription, setIsSubscription] = useState(false);

  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelStep, setCancelStep] = useState(0); // 0 = reason, 1 = retention
  const [cancelReason, setCancelReason] = useState(null);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        if (authLoading) return; // wait until auth resolves
        if (!user) {
          setLoading(false);
          navigate('/auth?redirect=/profile');
          return;
        }
        const [{ data: profile, error: profileError }, status] = await Promise.all([
          db.getProfile(user.id),
          paymentService.getPaymentStatus()
        ]);
        if (!mounted) return;

        if (profileError) {
          console.warn('Profile fetch error:', profileError);
        }

        if (profile) {
          // Use profile data from database
          setFullName(profile.full_name || '');
          setUsername(profile.username || '');
        } else {
          // No profile exists yet - pre-fill from user metadata (e.g., Google SSO)
          const userMetadata = user.user_metadata || {};
          setFullName(userMetadata.full_name || userMetadata.name || '');
          setUsername(userMetadata.username || '');
        }
        
        setLastPayment(status.lastPayment || null);
        if (status.hasPaid) {
          const p = status.lastPayment;
          const isSub = !!(p?.product_type?.includes('subscription'));
          const cancelled = p?.payment_status === 'subscription_cancelled';
          setIsSubscription(isSub);
          setPlanLabel(cancelled ? 'Pro (cancels at period end)' : (isSub ? 'Pro (subscription)' : 'Pro'));
        } else {
          setPlanLabel('Free');
          setIsSubscription(false);
        }
      } catch (e) {
        console.error('Profile load error:', e);
        setError(e.message || 'Failed to load profile');
      } finally {
        if (mounted) setLoading(false);
      }
    };
    load();
    return () => { mounted = false };
  }, [user, authLoading, navigate]);

  const handleSave = async () => {
    if (!user) return;
    setSaving(true);
    setError('');
    setMessage('');
    try {
      // 1) Update/create profiles table entry (upsert)
      const { data: profileData, error: upErr } = await db.updateProfile(user.id, { full_name: fullName, username });
      if (upErr) {
        console.error('Profile update error:', upErr);
        throw upErr;
      }
      console.log('Profile saved:', profileData);
      
      // 2) Update Supabase auth user metadata so it propagates across sessions
      const { error: authErr } = await supabase.auth.updateUser({ data: { full_name: fullName, username } });
      if (authErr) {
        console.warn('Auth metadata update warning:', authErr);
        // Don't throw - profile was saved, this is just extra
      }
      setMessage('Profile updated successfully!');
      // Optional: take user back after a brief delay
      setTimeout(() => navigate(-1), 1000);
    } catch (e) {
      console.error('Save error:', e);
      setError(e.message || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const openCancelModal = () => {
    setCancelStep(0);
    setCancelReason(null);
    setShowCancelModal(true);
  };

  const closeCancelModal = () => {
    setShowCancelModal(false);
    setTimeout(() => {
      setCancelStep(0);
      setCancelReason(null);
    }, 300);
  };

  const handleConfirmCancel = async () => {
    setCancelLoading(true);
    setError('');
    setMessage('');
    try {
      await paymentService.cancelSubscription();
      setMessage('Cancellation scheduled. Your plan remains active until the end of the current period.');
      setPlanLabel('Pro (cancels at period end)');
      closeCancelModal();
    } catch (e) {
      setError(e.message || 'Failed to cancel subscription');
    } finally {
      setCancelLoading(false);
    }
  };

  const handleUpgrade = async () => {
    setUpgradeLoading(true);
    setError('');
    try {
      const { url } = await paymentService.startIntroSubscription(window.location.origin, 'profile-upgrade');
      if (url) {
        window.location.href = url;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (e) {
      console.error('Upgrade error:', e);
      setError(e.message || 'Failed to start upgrade process');
      setUpgradeLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-6 h-6 border-2 border-black/20 border-t-black rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white flex items-center justify-center px-6 py-12">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-2xl bg-white/70 border border-black/10 rounded-2xl shadow p-8"
      >
        <div className="flex items-center justify-between mb-4">
          <button onClick={() => navigate(-1)} className="px-3 py-1.5 text-sm rounded-lg bg-black/5 hover:bg-black/10">Back</button>
        </div>
        <h1 className="text-2xl font-semibold mb-6">Your Profile</h1>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-100 rounded text-red-700 text-sm">{error}</div>
        )}
        {message && (
          <div className="mb-4 p-3 bg-green-50 border border-green-100 rounded text-green-700 text-sm">{message}</div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-black/60 mb-2">Full name</label>
            <input value={fullName} onChange={(e)=>setFullName(e.target.value)} className="w-full px-4 py-3 border border-black/10 rounded-xl bg-white/60" placeholder="Your name" />
          </div>
          <div>
            <label className="block text-sm font-medium text-black/60 mb-2">Username</label>
            <input value={username} onChange={(e)=>setUsername(e.target.value)} className="w-full px-4 py-3 border border-black/10 rounded-xl bg-white/60" placeholder="username" />
          </div>
        </div>

        <div className="mt-6 flex items-center gap-3">
          <button disabled={saving} onClick={handleSave} className="px-5 py-2 bg-black text-white rounded-xl disabled:opacity-50">{saving ? 'Saving...' : 'Save changes'}</button>
        </div>

        <div className="mt-10 pt-8 border-t border-black/10">
          <h2 className="text-lg font-semibold mb-3">Plan</h2>
          <div className="flex items-center justify-between bg-black/5 rounded-xl p-4">
            <div>
              <div className="text-sm text-black/60">Current plan</div>
              <div className="font-medium">{planLabel}</div>
            </div>
            {planLabel === 'Free' ? (
              <button 
                onClick={handleUpgrade}
                disabled={upgradeLoading}
                className="px-4 py-2 bg-black text-white rounded-xl hover:bg-black/80 transition-colors disabled:opacity-50"
              >
                {upgradeLoading ? 'Redirecting...' : 'Upgrade to Pro'}
              </button>
            ) : planLabel.startsWith('Pro') && !planLabel.includes('cancels') && (
              <button onClick={openCancelModal} className="px-4 py-2 text-sm text-red-600 hover:text-red-700 hover:bg-red-50 rounded-xl transition-colors">Cancel subscription</button>
            )}
          </div>
        </div>
      </motion.div>

      {/* Cancellation Retention Modal */}
      <AnimatePresence>
        {showCancelModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={closeCancelModal}
          >
            <motion.div
              initial={{ scale: 0.95, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.95, y: 20 }}
              className="bg-white rounded-2xl w-full max-w-md shadow-2xl overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Step 0: Ask reason */}
              {cancelStep === 0 && (
                <div>
                  <div className="px-6 pt-6 pb-4">
                    <h3 className="text-lg font-semibold text-gray-900 mb-1">Before you go...</h3>
                    <p className="text-sm text-gray-500">We'd love to know why so we can improve. What's the main reason?</p>
                  </div>

                  <div className="px-6 pb-2 space-y-2">
                    {CANCEL_REASONS.map((reason) => (
                      <button
                        key={reason.id}
                        onClick={() => {
                          setCancelReason(reason.id);
                          setCancelStep(1);
                        }}
                        className="w-full flex items-center gap-3 px-4 py-3 rounded-xl border border-gray-100 hover:border-gray-300 hover:bg-gray-50 transition-all text-left"
                      >
                        <span className="text-lg">{reason.icon}</span>
                        <span className="text-sm font-medium text-gray-700">{reason.label}</span>
                        <svg className="w-4 h-4 text-gray-300 ml-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </button>
                    ))}
                  </div>

                  <div className="px-6 py-4">
                    <button
                      onClick={closeCancelModal}
                      className="w-full py-2.5 text-sm font-medium text-gray-500 hover:text-gray-700 transition-colors"
                    >
                      Never mind, I'll stay
                    </button>
                  </div>
                </div>
              )}

              {/* Step 1: Retention pitch */}
              {cancelStep === 1 && cancelReason && (
                <div>
                  <div className="px-6 pt-6 pb-4">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      {RETENTION_MESSAGES[cancelReason].headline}
                    </h3>
                    <p className="text-sm text-gray-600 leading-relaxed mb-3">
                      {RETENTION_MESSAGES[cancelReason].body}
                    </p>
                    <div className="bg-blue-50 border border-blue-100 rounded-xl px-4 py-3">
                      <p className="text-sm text-blue-800">
                        {RETENTION_MESSAGES[cancelReason].offer}
                      </p>
                    </div>
                  </div>

                  {/* What you'll lose */}
                  <div className="px-6 pb-4">
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">What you'll lose</p>
                    <div className="space-y-2">
                      {[
                        'AI-powered file & prompt generation',
                        'Complete project dashboard',
                        'Design specification tools',
                        'Visual PRD editor & analytics',
                        'All future feature updates',
                      ].map((feature, i) => (
                        <div key={i} className="flex items-center gap-2 text-sm text-gray-600">
                          <svg className="w-4 h-4 text-red-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                          {feature}
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="px-6 pb-6 space-y-2">
                    <button
                      onClick={closeCancelModal}
                      className="w-full py-3 bg-black text-white font-semibold rounded-xl hover:bg-gray-800 transition-colors"
                    >
                      Keep my Pro plan
                    </button>
                    <button
                      onClick={handleConfirmCancel}
                      disabled={cancelLoading}
                      className="w-full py-2.5 text-sm text-gray-400 hover:text-red-500 transition-colors disabled:opacity-50"
                    >
                      {cancelLoading ? 'Cancelling...' : 'Cancel anyway'}
                    </button>
                  </div>
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Profile;


